import './PresetDropdown.scss';
import * as Dfns from 'date-fns/fp';
import * as R from 'ramda';
import { ReactNode, useCallback, useEffect, useMemo, useState } from 'react';
import { OverlayTrigger, Popover } from 'react-bootstrap';
import {
  MdAdd,
  MdCheck,
  MdContentCopy,
  MdDelete,
  MdEdit,
  MdMoreVert,
  MdPreview,
  MdStar
} from 'react-icons/md';
import { useAppSelector } from '../../redux';
import { SetError, useSetError } from '../../redux/modals';
import * as UserRedux from '../../redux/user';
import {
  getInvalidAnalyticsDetails,
  getInvalidGranularityDetailsFromComparingPresets,
  getInvalidGranularityDetailsFromPresetAndCols,
  InvalidGranularityDetails,
  makeInvalidDimMsg
} from '../../utils/columnMetricSystem/configUtils';
import { useExperimentFlag } from '../../utils/experiment-context';
import { useExperimentalAnalyticsCols } from '../../utils/hooks/useExperimentalAnalyticsCols';
import { STANDARD_GROUP_NAME } from '../../utils/performance-utils';
import { Button } from '../Button/Button';
import { Appearance, Intent, Placement, Size, SizeType } from '../designConstants';
import { IconButton } from '../IconButton';
import { Dropdown } from '../Dropdown/Dropdown';
import { InteractiveDiv } from '../InteractiveEl';
import { transformEmailToFullName } from '../MetricsTable/metrics-table-utils';
import RenameIcon from '../RenameModal/RenameIcon.svg?react';

const canDuplicatePreset = (
  isAdmin: boolean,
  preset: any,
  setError: (error: { message: string }) => void,
  duplicateItemOnClick: (preset: any) => void
): void => {
  if (!preset) return;
  const isAdminLocked =
    ('admin_lock' in preset && preset.admin_lock) ||
    ('globalOptions' in preset && preset.globalOptions?.admin_lock);
  if (!isAdmin && isAdminLocked) {
    setError({ message: 'Cannot duplicate admin-locked presets!' });
    return;
  }
  duplicateItemOnClick(preset);
};

const canSetAsDefault = ({
  isAdmin,
  preset,
  presets,
  setAsDefault,
  setError
}: {
  isAdmin: boolean;
  preset: any;
  presets: any[];
  setAsDefault: (preset: any) => void;
  setError: SetError;
}): void => {
  if (!preset) return;
  if (!isAdmin) {
    for (const currentPreset of presets) {
      if (currentPreset?.globalOptions?.admin_lock) {
        setError({
          message: `${currentPreset.name} is currently the default preset and has an admin lock. You do not have permissions to override it as the default preset.`
        });
        return;
      }
    }
  }
  (async () => {
    await setAsDefault(preset);
    setError({
      message: `${preset.name} (${preset.id}) has been set as the default ${preset.mediatype} preset.`,
      title: 'Success!',
      intent: Intent.SUCCESS
    });
  })();
};

const EMPTY_COLS: string[] = [];

interface PresetDropdownProps {
  size?: SizeType;
  addPresetOnClick?: () => void;
  addPresetText?: string;
  allowNoSelections?: boolean;
  applyOnClick: (preset: any) => void;
  cancelOnClick: () => void;
  className?: string;
  defaultPresetNames?: string[];
  deleteItemOnClick?: (presetID: number) => void;
  duplicateItemOnClick?: (preset: any) => void;
  editItemOnClick?: (preset: any) => void;
  hasPresetGroups?: boolean;
  hasTicks?: boolean;
  label?: string;
  analyticsIsSource?: boolean;
  presets: any[];
  presetType?: 'page' | 'table' | 'filter' | string;
  renameItemOnClick?: (presetID: any) => void;
  selectedPreset?: any;
  setAsDefault?: (preset: any) => void;
  subTitles?: string[];
  // the other side's cols (filter dims for a table preset, table cols for a filter preset); each preset is
  // checked against them together with its own cols
  otherPresetCols?: string[];
  channel?: string;
  invalidGranularityDetailsFromDeliveryDim?: InvalidGranularityDetails;
}

export const PresetDropdown = ({
  addPresetOnClick = () => {},
  addPresetText = 'Create New Preset',
  allowNoSelections = false,
  applyOnClick = () => {},
  cancelOnClick = () => {},
  className = '',
  defaultPresetNames = [],
  deleteItemOnClick = () => {},
  duplicateItemOnClick = () => {},
  editItemOnClick = () => {},
  hasPresetGroups = false,
  hasTicks = true,
  label = 'Load Preset',
  analyticsIsSource = false,
  presets,
  presetType,
  renameItemOnClick = () => {},
  selectedPreset,
  setAsDefault,
  subTitles,
  otherPresetCols = EMPTY_COLS,
  channel = '',
  invalidGranularityDetailsFromDeliveryDim = {},
  size = Size.LG
}: PresetDropdownProps) => {
  const isAdmin = useAppSelector(UserRedux.isAdminSelector);
  const isInternal = useAppSelector(UserRedux.isInternalSelector);
  const preventPresetCreation = useExperimentFlag('preventPresetCreation') as boolean;
  const setError = useSetError();
  const expAnalyticsCols = useExperimentalAnalyticsCols();
  const [presetMap, presetGroupMap] = useMemo(() => {
    let standardGroupCount = 0;
    const pMap: Record<string, any> = {};
    const pgMap: Record<string, boolean> = { All: true };
    for (const preset of presets) {
      pMap[preset.id] = preset;
      if (preset.presetGroup) {
        if (preset.presetGroup === STANDARD_GROUP_NAME) {
          standardGroupCount++;
        }
        pgMap[preset.presetGroup] = true;
      }
    }
    return [
      pMap,
      {
        ...pgMap,
        ...(standardGroupCount > 1 ? { Custom: true } : {})
      }
    ];
  }, [presets]);

  const [selectedOptionTrigger, setSelectedOptionTrigger] = useState<number | undefined>(undefined);
  const [showPresetDropdown, setShowPresetDropdown] = useState<boolean>(false);
  const [tempSelectedPreset, setTempSelectedPreset] = useState<any | null | undefined>(
    selectedPreset
  );
  const [tempSelectedPresetGroup, setTempSelectedPresetGroup] = useState<any | null | undefined>(
    selectedPreset ? selectedPreset.presetGroup : undefined
  );

  useEffect(() => {
    if (selectedPreset) {
      setTempSelectedPreset(selectedPreset);
      setTempSelectedPresetGroup(selectedPreset.presetGroup);
    } else {
      setTempSelectedPreset(null);
      setTempSelectedPresetGroup(null);
    }
  }, [selectedPreset]);

  const getIsSelected = useCallback(
    (preset: any): boolean => {
      if (tempSelectedPreset === null) return false;
      if (tempSelectedPreset) return tempSelectedPreset.id === preset.id;
      return Boolean(selectedPreset && selectedPreset.id === preset.id);
    },
    [tempSelectedPreset, selectedPreset]
  );

  const filteredPresets = useMemo(() => {
    const result: any[] = [];
    if (tempSelectedPresetGroup === 'All' || R.isNil(tempSelectedPresetGroup)) {
      return presets;
    }
    for (const preset of presets) {
      if (tempSelectedPresetGroup === 'Custom' && preset.presetGroup !== STANDARD_GROUP_NAME) {
        result.push(preset);
      } else if (preset.presetGroup === tempSelectedPresetGroup) {
        result.push(preset);
      }
    }
    return result;
  }, [presets, tempSelectedPresetGroup]);

  const presetOptions = useMemo(
    () =>
      R.map(preset => {
        const isDefault =
          (!R.isNil(preset) && !R.isNil(preset.id) && preset.id <= 0) ||
          (Boolean(preset?.name) && defaultPresetNames.includes(preset.name.toLowerCase()));
        const last_modified =
          !R.isNil(preset) && !R.isNil(preset.last_modified || preset.lastmodified)
            ? Dfns.format(
                'MM/dd/yyyy',
                new Date((preset.last_modified || preset.lastmodified).slice(0, 10))
              )
            : 'N/A';
        let last_user = 'N/A';
        if (!R.isNil(preset) && !R.isNil(preset.last_user || preset.lastuser)) {
          last_user = transformEmailToFullName(preset.last_user || preset.lastuser);
        } else if (!R.isNil(preset) && !R.isNil(preset.id) && preset.id <= 0) {
          last_user = 'Default Preset';
        }
        const isInternalPreset = R.defaultTo(
          '',
          R.defaultTo(preset.created_by, preset.createdBy)
        ).endsWith('@tinuiti.com');

        const [hasInvalidAnalyticsDims, invalidAnalyticDimPrettyNames] = getInvalidAnalyticsDetails(
          preset,
          analyticsIsSource,
          expAnalyticsCols,
          presetType
        );

        const [invalidPrettyDimsFromPreset, activeInvalidDimsFromOtherPreset] =
          getInvalidGranularityDetailsFromPresetAndCols(
            preset,
            otherPresetCols,
            channel,
            presetType
          );

        const [invalidPrettyDimsFromDelivery, activeInvalidDimsFromDelivery] =
          getInvalidGranularityDetailsFromComparingPresets(
            preset,
            invalidGranularityDetailsFromDeliveryDim,
            presetType
          );

        const isDisabled =
          preset.disabled ||
          (analyticsIsSource && hasInvalidAnalyticsDims) ||
          (invalidPrettyDimsFromPreset && activeInvalidDimsFromOtherPreset) ||
          (invalidPrettyDimsFromDelivery && activeInvalidDimsFromDelivery);
        const showToolTip = Boolean(
          (analyticsIsSource && hasInvalidAnalyticsDims) ||
          (invalidPrettyDimsFromPreset && activeInvalidDimsFromOtherPreset) ||
          (invalidPrettyDimsFromDelivery && activeInvalidDimsFromDelivery)
        );

        let tooltipMessage;
        if (showToolTip) {
          const combinedPrettyDims = R.uniq([
            ...(invalidPrettyDimsFromPreset ?? []),
            ...(invalidPrettyDimsFromDelivery ?? [])
          ]);
          tooltipMessage = makeInvalidDimMsg({
            prettyDims: hasInvalidAnalyticsDims
              ? invalidAnalyticDimPrettyNames
              : combinedPrettyDims,
            isInvalidAnalyticsDim: hasInvalidAnalyticsDims,
            conflictingTableDims: presetType === 'filter' ? activeInvalidDimsFromOtherPreset : [],
            conflictingFilterDims: presetType === 'table' ? activeInvalidDimsFromOtherPreset : [],
            conflictingDeliveryDims: activeInvalidDimsFromDelivery ?? [],
            type: presetType
          });
        }

        return {
          className: selectedOptionTrigger === preset.id ? 'dropdown-open' : '',
          disabled: isDisabled,
          simpleTooltip: tooltipMessage,
          simpleTooltipVerticalShift: '-55px',
          label: preset && preset.temporary ? 'Temporary Preset' : preset.name,
          value: `${preset.id}`,
          itemOptions:
            (!isInternal && isInternalPreset) || preventPresetCreation ? undefined : (
              <OverlayTrigger
                show={selectedOptionTrigger === preset.id}
                placement={Placement.LEFT_START}
                overlay={
                  <Popover id={`preset-options-${preset.id}`}>
                    <div className='preset-options-preset-dropdown'>
                      <PresetOptionItem
                        onClick={() => {
                          setSelectedOptionTrigger(undefined);
                          setShowPresetDropdown(false);
                          editItemOnClick(preset);
                        }}
                      >
                        <div className='preset-option-icon'>
                          {isAdmin || !preset.admin_lock ? <MdEdit /> : <MdPreview />}
                        </div>
                        <div className='preset-option-text'>
                          {isAdmin || !preset.admin_lock ? 'Edit' : 'View'}
                        </div>
                      </PresetOptionItem>
                      {(isAdmin || !preset.admin_lock) && (
                        <PresetOptionItem
                          onClick={() => {
                            if (isDefault) {
                              setError({ message: 'Cannot rename default presets!' });
                            } else {
                              setSelectedOptionTrigger(undefined);
                              setShowPresetDropdown(false);
                              renameItemOnClick(preset);
                            }
                          }}
                        >
                          <div className='preset-option-icon'>
                            <RenameIcon />
                          </div>
                          <div className='preset-option-text'>Rename</div>
                        </PresetOptionItem>
                      )}
                      {(isAdmin || !preset.admin_lock) && (
                        <PresetOptionItem
                          onClick={() => {
                            if (isDefault && setError) {
                              setError({ message: 'Cannot delete default presets' });
                            } else {
                              setSelectedOptionTrigger(undefined);
                              setShowPresetDropdown(false);
                              deleteItemOnClick(preset.id);
                            }
                          }}
                        >
                          <div className='preset-option-icon'>
                            <MdDelete />
                          </div>
                          <div className='preset-option-text'>Delete</div>
                        </PresetOptionItem>
                      )}
                      <PresetOptionItem
                        onClick={() => {
                          setSelectedOptionTrigger(undefined);
                          setShowPresetDropdown(false);
                          canDuplicatePreset(isAdmin, preset, setError, duplicateItemOnClick);
                        }}
                      >
                        <div className='preset-option-icon'>
                          <MdContentCopy />
                        </div>
                        <div className='preset-option-text'>Duplicate</div>
                      </PresetOptionItem>
                      {setAsDefault &&
                        (preset?.globalOptions?.isDefault ? (
                          <div className='preset-option disabled-delete'>
                            <div className='preset-option-icon'>
                              <MdCheck />
                            </div>
                            <div className='preset-option-text'>Default Preset</div>
                          </div>
                        ) : (
                          <PresetOptionItem
                            onClick={() => {
                              setSelectedOptionTrigger(undefined);
                              setShowPresetDropdown(false);
                              canSetAsDefault({ isAdmin, preset, presets, setAsDefault, setError });
                            }}
                          >
                            <div className='preset-option-icon'>
                              <MdCheck />
                            </div>
                            <div className='preset-option-text'>Set as Default</div>
                          </PresetOptionItem>
                        ))}
                      <div className='preset-last-modified-container'>
                        <div className='preset-last-modified-text'>Last updated:</div>
                        <div className='preset-last-modified-text'>
                          {last_modified} by {last_user}
                        </div>
                      </div>
                    </div>
                  </Popover>
                }
              >
                <IconButton
                  className='preset-option-expander'
                  appearance={Appearance.EMPTY}
                  onClick={() =>
                    setSelectedOptionTrigger(
                      selectedOptionTrigger === preset.id ? undefined : preset.id
                    )
                  }
                >
                  <MdMoreVert />
                </IconButton>
              </OverlayTrigger>
            ),
          selected: getIsSelected(preset)
        };
      }, filteredPresets),
    [
      defaultPresetNames,
      deleteItemOnClick,
      duplicateItemOnClick,
      editItemOnClick,
      filteredPresets,
      getIsSelected,
      isAdmin,
      isInternal,
      analyticsIsSource,
      presets,
      presetType,
      renameItemOnClick,
      selectedOptionTrigger,
      setAsDefault,
      setError,
      preventPresetCreation,
      expAnalyticsCols,
      otherPresetCols,
      channel,
      invalidGranularityDetailsFromDeliveryDim
    ]
  );

  const presetGroupOptions = useMemo(() => {
    const selectedGroup = presets.find(preset => getIsSelected(preset))?.presetGroup;
    const presetGroups = R.uniq(
      (R.defaultTo([], presets) as any[]).map(preset => preset.presetGroup)
    );
    const options = [
      {
        label: 'All',
        value: 'All',
        selected:
          (R.isNil(selectedGroup) && R.isNil(tempSelectedPresetGroup)) ||
          (tempSelectedPresetGroup ? tempSelectedPresetGroup === 'All' : selectedGroup === 'All')
      },
      ...(presetGroupMap.Custom
        ? [
            {
              label: 'Custom',
              value: 'Custom',
              selected: tempSelectedPresetGroup
                ? tempSelectedPresetGroup === 'Custom'
                : selectedGroup === 'Custom'
            }
          ]
        : [])
    ];
    for (const presetGroup of presetGroups) {
      if (presetGroup && presetGroup.length) {
        options.push({
          label: presetGroup,
          selected: tempSelectedPresetGroup
            ? tempSelectedPresetGroup === presetGroup
            : selectedGroup === presetGroup,
          value: presetGroup
        });
      }
    }
    return options;
  }, [getIsSelected, presetGroupMap, presets, tempSelectedPresetGroup]);

  const showAddButton = !preventPresetCreation;
  const showRemoveButton = allowNoSelections && Boolean(selectedPreset);

  const footerContent =
    showAddButton || showRemoveButton ? (
      <>
        {showRemoveButton && (
          <div className='remove-preset-section'>
            <Button
              size={Size.LG}
              className='remove-preset-button'
              appearance={Appearance.EMPTY}
              onClick={e => {
                e.stopPropagation();
                applyOnClick(undefined);
                setTempSelectedPreset(null);
                setTempSelectedPresetGroup(null);
                setShowPresetDropdown(false);
              }}
            >
              Remove Preset
            </Button>
          </div>
        )}
        {showAddButton && (
          <div className='add-new-preset-section'>
            <Button
              size={Size.LG}
              className='add-new-preset-button'
              leadingElement={<MdAdd />}
              appearance={Appearance.EMPTY}
              onClick={() => addPresetOnClick()}
            >
              {addPresetText}
            </Button>
          </div>
        )}
      </>
    ) : null;

  return (
    <div className={`preset-dropdown ${className} ${selectedPreset ? 'preset-selected' : ''}`}>
      {presets && (
        <Dropdown
          overlayPlacement={Placement.RIGHT}
          size={size}
          applyOnClick={selectedItems => {
            applyOnClick(
              allowNoSelections &&
                (R.isNil(selectedItems) ||
                  R.isEmpty(selectedItems) ||
                  (selectedItems.length === 1 &&
                    (R.isNil(selectedItems[0]) || R.isEmpty(selectedItems[0]))))
                ? undefined
                : tempSelectedPreset || selectedPreset
            );
          }}
          cancelOnClick={() => cancelOnClick()}
          footer={footerContent ?? undefined}
          footerOnClick={() => {}}
          hasFilterBar={false}
          hasTicks={hasTicks}
          label={<div>{label}</div>}
          leadingIcon={<MdStar />}
          menuItems={hasPresetGroups ? [presetGroupOptions, presetOptions] : presetOptions}
          multiSelect={false}
          onChange={value => {
            if (tempSelectedPreset && `${tempSelectedPreset.id}` === value) {
              setTempSelectedPreset(null);
              setTempSelectedPresetGroup(null);
            } else if (value && presetMap[value]) {
              setTempSelectedPreset(presetMap[value]);
              setTempSelectedPresetGroup(presetMap[value].presetGroup);
            }
            if (presetGroupMap[value]) {
              setTempSelectedPresetGroup(value);
            }
          }}
          show={showPresetDropdown}
          setShow={setShowPresetDropdown}
          subTitles={subTitles}
          onToggle={isOpen => {
            if (!R.isNil(selectedOptionTrigger)) {
              setSelectedOptionTrigger(undefined);
              setShowPresetDropdown(true);
            } else {
              setShowPresetDropdown(isOpen);
            }
          }}
          options={[]}
          value=''
        />
      )}
    </div>
  );
};

interface PresetOptionItemProps {
  onClick: () => void;
  children: ReactNode;
}

const PresetOptionItem = ({ onClick, children }: PresetOptionItemProps) => (
  <InteractiveDiv className='preset-option' onClick={onClick}>
    {children}
  </InteractiveDiv>
);
